import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import {
  OptroWorkpaperMatch,
  OptroWorkpaperMatchSchema,
} from "../models/audit/optroSync";

const MatchWorkpapersResponseSchema = z.object({
  matches: z.array(OptroWorkpaperMatchSchema),
});

// A single request carrying hundreds of refs risks a gateway timeout on the
// backend (which calls out to Optro per ref) — chunk requests so each stays
// small, and cap how many chunks are in flight at once.
const CHUNK_SIZE = 50;
const CONCURRENCY = 2;

function chunkRefs(refs: string[]): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < refs.length; i += CHUNK_SIZE) {
    chunks.push(refs.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
}

function emptyMatchesFor(refs: string[]): OptroWorkpaperMatch[] {
  return refs.map((ref) => ({ controlExternalRef: ref, workpapers: [] }));
}

// Fetches matches for one chunk of refs. On failure, falls back to an empty
// match per ref in this chunk so the review table still renders those rows
// (as "no workpaper found") instead of the whole flow hanging or erroring.
async function fetchMatchesChunk(
  refs: string[],
): Promise<OptroWorkpaperMatch[]> {
  try {
    const response = await fetchFastApi({
      method: "POST",
      path: "/controls/external/match-workpapers",
      body: { externalRefs: refs },
    });
    return MatchWorkpapersResponseSchema.parse(response).matches;
  } catch (error) {
    console.error("Failed to match workpapers for a chunk of refs:", error);
    return emptyMatchesFor(refs);
  }
}

// Runs the chunked legacy match flow to completion, invoking `onProgress`
// with the cumulative results after each chunk group settles. Used by the
// agentic match job hook as its fallback path when the agentic job can't
// even start.
export async function fetchMatchesChunked(
  refs: string[],
  options: {
    isCancelled: () => boolean;
    onProgress: (matches: OptroWorkpaperMatch[]) => void;
  },
): Promise<void> {
  const chunks = chunkRefs(refs);
  const collected: OptroWorkpaperMatch[] = [];

  if (chunks.length === 0) {
    if (!options.isCancelled()) options.onProgress([]);
    return;
  }

  for (let i = 0; i < chunks.length; i += CONCURRENCY) {
    if (options.isCancelled()) return;
    const group = chunks.slice(i, i + CONCURRENCY);
    const groupResults = await Promise.all(group.map(fetchMatchesChunk));
    if (options.isCancelled()) return;
    for (const result of groupResults) collected.push(...result);
    options.onProgress([...collected]);
  }
}
